// ============================================================
// Profileur : la liste des serveurs, groupe par groupe
// ============================================================
// Fonctions pures (testées par profiler.servers.test.ts). La page interroge le satellite de chaque
// groupe de jeu du projet (Nova, Iris ; un seul appel sans groupe pour Paladium), puis rapproche les
// serveurs qui envoient des mesures de ceux que la Console connaît (Pterodactyl) : un serveur de la
// Console qui n'a jamais rien envoyé s'affiche « aucune donnée reçue », pas « en panne ».
// ============================================================
import type { ServerList, ServerSummary } from './profiler.types';
import { NEVER_SEEN, compareByUrgency, serverHealth, type Health, type Light } from './profiler.health.ts';

/** Serveur de jeu tel que la Console le connaît (GET /api/console/servers). */
export interface KnownServer {
  identifier: string;
  uuid: string;
  name: string;
  status?: string;
  suspended?: boolean;
}

/** Ce que la page a obtenu pour un groupe de jeu (`key` null : projet sans groupes). */
export interface GroupResult {
  key: string | null;
  label: string | null;
  /** Mesures du satellite de ce groupe ; null si l'appel a échoué ou si le module y est éteint. */
  list: ServerList | null;
  /** Le satellite répond que le module profiler n'est pas activé (404) : ce n'est pas une erreur. */
  moduleOff: boolean;
  /**
   * Le satellite ne connaît pas la route (404 sans rapport avec le module) : il n'a pas encore la
   * version qui porte le profileur. Ce n'est pas une panne non plus.
   */
  unavailable?: boolean;
  /** Message d'échec de l'appel, null sinon. */
  error: string | null;
  /** Serveurs de la Console ; null si elle n'a pas pu être lue (Pterodactyl injoignable, droit absent). */
  known: KnownServer[] | null;
}

export interface Row {
  /** Identifiant unique de la ligne dans la page. */
  id: string;
  /** Clé du serveur côté profileur ; null pour un serveur jamais vu (pas de page de détail). */
  key: string | null;
  group: string | null;
  groupLabel: string | null;
  /** Nom affiché : celui de la Console quand le serveur y est retrouvé, sinon celui qu'il annonce. */
  name: string;
  /** Nom d'instance annoncé par le serveur, s'il diffère du nom affiché. */
  instance: string | null;
  model: string | null;
  server: ServerSummary | null;
  health: Health;
  /** État du serveur d'après la Console (running, offline...), null si elle ne le connaît pas. */
  consoleStatus: string | null;
}

/** La réponse d'un appel dit-elle « module éteint » ? (404 du ModuleGateFilter du satellite) */
export function isModuleOff(status: number | undefined, error: string | undefined): boolean {
  return status === 404 && /module/i.test(error ?? '');
}

/**
 * Ce qu'un 404 veut dire, d'après son message.
 *
 * - `moduleOff` : le satellite connaît le profileur, son module est éteint (« Le module 'profiler'... ») ;
 * - `missing` : le satellite connaît le profileur, pas ce serveur (« Aucune mesure recue de ce serveur. ») ;
 * - `unavailable` : tout autre 404 (« HTTP 404 Not Found », page d'erreur, message vide). Le satellite
 *   ne connaît pas la route : il est d'une version d'avant le profileur. Le panel est livré avant les
 *   satellites, et tous ne sont pas mis à jour le même jour : ce n'est pas une panne.
 */
export type NotFoundKind = 'moduleOff' | 'missing' | 'unavailable';

export function notFoundKind(error: string | undefined): NotFoundKind {
  const text = error ?? '';
  if (/module/i.test(text)) return 'moduleOff';
  if (/aucune mesure|serveur inconnu/i.test(text)) return 'missing';
  return 'unavailable';
}

/** Réponse de la LISTE d'un satellite, rangée : mesures, module éteint, profileur pas encore là, ou vraie erreur. */
export function listOutcome(res: { data?: ServerList; error?: string; status?: number }): Pick<GroupResult, 'list' | 'moduleOff' | 'unavailable' | 'error'> {
  if (res.data) return { list: res.data, moduleOff: false, unavailable: false, error: null };
  if (res.status === 404) {
    // La liste n'a pas de serveur a ne pas trouver : hors « module eteint », un 404 est un satellite sans la route.
    const moduleOff = notFoundKind(res.error) === 'moduleOff';
    return { list: null, moduleOff, unavailable: !moduleOff, error: null };
  }
  return { list: null, moduleOff: false, unavailable: false, error: res.error ?? 'Réponse illisible' };
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Lignes d'un groupe. Rapprochement avec la Console par l'identifiant Pterodactyl (`P_SERVER_UUID`,
 * que le mod envoie) ; à défaut d'identifiant, par le nom exact.
 */
export function groupRows(group: GroupResult, timeZone?: string): Row[] {
  const rows: Row[] = [];
  const known = group.known ?? [];
  const matched = new Set<string>();
  const prefix = group.key ?? '';

  for (const server of group.list?.servers ?? []) {
    const k = known.find((c) => (server.id ? c.uuid.toLowerCase() === server.id.toLowerCase() : sameName(c.name, server.name)));
    if (k) matched.add(k.uuid);
    const health = serverHealth(server, group.list!.now, timeZone, group.list!.startupGraceSeconds);
    const name = k?.name ?? server.name;
    rows.push({
      id: `${prefix}|${server.key}`,
      key: server.key,
      group: group.key,
      groupLabel: group.label,
      name,
      instance: sameName(name, server.name) ? null : server.name,
      model: server.model,
      server,
      health: withConsole(health, k?.status ?? null),
      consoleStatus: k?.status ?? null,
    });
  }

  // Connus de la Console, jamais vus par le profileur. Sans mesures du tout pour ce groupe (appel en
  // échec ou module éteint), on ne les liste pas : le bandeau du groupe dit pourquoi.
  if (group.list) {
    for (const k of known) {
      if (matched.has(k.uuid)) continue;
      rows.push({
        id: `${prefix}|console:${k.uuid}`,
        key: null,
        group: group.key,
        groupLabel: group.label,
        name: k.name,
        instance: null,
        model: null,
        server: null,
        health: withConsole(NEVER_SEEN, k.status ?? null),
        consoleStatus: k.status ?? null,
      });
    }
  }
  return rows;
}

/** Sans mesure à juger, la Console peut au moins dire que le serveur est arrêté : on le dit. */
export function withConsole(health: Health, status: string | null): Health {
  if (health.light !== 'none' || status !== 'offline') return health;
  return { ...health, reason: `${health.reason} La Console indique que ce serveur est arrêté.` };
}

/** Toutes les lignes de la page, les serveurs en difficulté d'abord. */
export function allRows(groups: GroupResult[], timeZone?: string): Row[] {
  return groups.flatMap((g) => groupRows(g, timeZone)).sort(compareByUrgency);
}

/**
 * Le tableau ne liste que les serveurs qui ont déjà envoyé une mesure. Ceux que seule la Console
 * connaît (proxy compris) sont regroupés à part, repliés : lors d'un essai sur un seul serveur, sa
 * ligne ne doit pas se perdre parmi des dizaines de lignes grises.
 */
export function splitRows(rows: Row[]): { measured: Row[]; neverSeen: Row[] } {
  const measured: Row[] = [];
  const neverSeen: Row[] = [];
  for (const row of rows) (row.server ? measured : neverSeen).push(row);
  neverSeen.sort((a, b) => a.name.localeCompare(b.name, 'fr', { numeric: true, sensitivity: 'base' }));
  return { measured, neverSeen };
}

/** Compte des voyants, pour la rangée de chiffres en tête de page. */
export function countLights(rows: Row[]): Record<Light, number> {
  const out: Record<Light, number> = { ok: 0, watch: 0, bad: 0, none: 0 };
  for (const r of rows) out[r.health.light]++;
  return out;
}

/** Adresse de la page de détail d'un serveur (le groupe voyage dans `?g=`, comme ailleurs dans le panel). */
export function serverPath(row: Pick<Row, 'key' | 'group'>): string | null {
  if (!row.key) return null;
  return `/deploiement/profileur/${encodeURIComponent(row.key)}${row.group ? `?g=${encodeURIComponent(row.group)}` : ''}`;
}
